import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { CreditCard, CheckCircle, Shield, Zap, BarChart3, Mail, MessageSquare, Loader2, ArrowRight, Lock, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientActivation({ slug, token, onActivated, suspended = false }) {
  const { t } = useTranslation('dashboard');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, [slug]);

  // Check if returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/api/client/payments/status/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('Failed to fetch payment status', err);
    }
    setLoading(false);
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) return;
    try {
      const res = await fetch(`${API}/api/client/payments/checkout/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        await fetchStatus();
        if (onActivated) onActivated();
        return;
      }
      if (data.status === 'expired') return;
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    } catch (err) {
      console.error('Poll error', err);
    }
  };

  const initiatePayment = async (packageId) => {
    setCheckingOut(packageId);
    try {
      const res = await fetch(`${API}/api/client/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: packageId,
          slug,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Checkout error', err);
    }
    setCheckingOut(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const pricing = status?.pricing || {};
  const setupPaid = status?.setup_paid;
  const features = [
    { icon: BarChart3, label: t('activation.features.dashboard') },
    { icon: Zap, label: t('activation.features.social') },
    { icon: Mail, label: t('activation.features.email') },
    { icon: MessageSquare, label: t('activation.features.sms') },
    { icon: Shield, label: t('activation.features.reputation') },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="activation-screen">
      <div className="max-w-2xl w-full">
        {/* Suspension Banner */}
        {suspended && (
          <div className="bg-red-900/20 border border-red-800 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="suspended-banner">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-red-300 font-medium">{t('activation.suspendedTitle')}</p>
              <p className="text-red-400/70 text-sm">{t('activation.suspendedDesc')}</p>
            </div>
          </div>
        )}

        {/* Update Payment Method Button (for suspended accounts) */}
        {suspended && (
          <div className="bg-gray-100 border border-gray-200 rounded-xl p-5 mb-6">
            <h3 className="text-gray-900 font-semibold mb-3">{t('activation.updatePaymentMethod')}</h3>
            <Button
              data-testid="update-payment-btn"
              onClick={async () => {
                setCheckingOut('update');
                try {
                  const res = await fetch(`${API}/api/client/payments/methods/${slug}/setup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ origin_url: window.location.origin }),
                  });
                  const data = await res.json();
                  if (data.url) window.location.href = data.url;
                } catch (err) { console.error(err); }
                setCheckingOut(null);
              }}
              disabled={checkingOut === 'update'}
              className="w-full bg-blue-600 hover:bg-blue-700 h-11"
            >
              {checkingOut === 'update' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              {t('activation.addUpdateCard')}
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${suspended ? 'bg-red-600/20' : 'bg-blue-600/20'} mb-4`}>
            {suspended ? <AlertTriangle className="w-8 h-8 text-red-400" /> : <CreditCard className="w-8 h-8 text-blue-400" />}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{suspended ? t('activation.restoreServices') : t('activation.activateDashboard')}</h1>
          <p className="text-gray-500">
            {t('activation.subtitle', { businessName: status?.business_name || t('activation.yourBusiness') })}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className={`grid gap-4 mb-8 ${pricing.setup_fee > 0 ? 'md:grid-cols-2' : 'max-w-md mx-auto'}`}>
          {/* Setup Fee — only show for setup+monthly billing */}
          {pricing.setup_fee > 0 && (
          <div className={`rounded-xl border p-6 ${setupPaid ? 'bg-green-900/10 border-green-800/50' : 'bg-gray-100 border-gray-200'}`}
            data-testid="setup-fee-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('activation.platformSetup')}</h3>
              {setupPaid && <CheckCircle className="w-5 h-5 text-green-400" />}
            </div>
            <div className="mb-4">
              <span className="text-3xl font-bold text-gray-900">${pricing.setup_fee?.toFixed(0)}</span>
              <span className="text-gray-500 text-sm ml-1">{t('activation.oneTime')}</span>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {t('activation.platformSetupDesc')}
            </p>
            {setupPaid ? (
              <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                <CheckCircle className="w-4 h-4" /> {t('activation.paid')}
              </div>
            ) : (
              <Button
                data-testid="pay-setup-btn"
                onClick={() => initiatePayment('setup')}
                disabled={checkingOut === 'setup'}
                className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              >
                {checkingOut === 'setup' ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('activation.processing')}</>
                ) : (
                  <>{t('activation.pay', { amount: pricing.setup_fee?.toFixed(0) })} <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            )}
            <p className="text-gray-400 text-xs mt-2 text-center">
              {!setupPaid && (
                <>
                  <span className="block text-gray-500">{t('activation.setupBreakdown', { base: pricing.setup_fee?.toFixed(2), fee: (pricing.setup_total - pricing.setup_fee)?.toFixed(2), pct: pricing.cc_fee_percent })}</span>
                  <span className="text-gray-900 font-medium">{t('activation.totalLabel', { total: pricing.setup_total?.toFixed(2) })}</span>
                </>
              )}
            </p>
          </div>
          )}

          {/* Monthly Subscription */}
          <div className={`rounded-xl border p-6 ${(pricing.setup_fee > 0 && !setupPaid) ? 'bg-gray-100 border-gray-200 opacity-60' : 'bg-gray-100 border-blue-600/50'}`}
            data-testid="monthly-fee-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('activation.monthlyService')}</h3>
              {(pricing.setup_fee > 0 && !setupPaid) && <Lock className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="mb-4">
              <span className="text-3xl font-bold text-gray-900">${pricing.monthly_fee?.toFixed(0)}</span>
              <span className="text-gray-500 text-sm ml-1">{t('activation.perMonth')}</span>
            </div>
            <p className="text-gray-500 text-sm mb-4">
              {t('activation.monthlyServiceDesc')}
            </p>
            {(pricing.setup_fee > 0 && !setupPaid) ? (
              <div className="text-gray-400 text-sm text-center py-2">
                {t('activation.paySetupFirst')}
              </div>
            ) : (
              <Button
                data-testid="pay-monthly-btn"
                onClick={() => initiatePayment('monthly')}
                disabled={checkingOut === 'monthly'}
                className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              >
                {checkingOut === 'monthly' ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('activation.processing')}</>
                ) : (
                  <>{t('activation.subscribe', { amount: pricing.monthly_fee?.toFixed(0) })} <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            )}
            <p className="text-gray-400 text-xs mt-2 text-center">
              {(pricing.setup_fee === 0 || setupPaid) && (
                <>
                  <span className="block text-gray-500">{t('activation.monthlyBreakdown', { base: pricing.monthly_fee?.toFixed(2), fee: (pricing.monthly_total - pricing.monthly_fee)?.toFixed(2), pct: pricing.cc_fee_percent })}</span>
                  <span className="text-gray-900 font-medium">{t('activation.totalMonthly', { total: pricing.monthly_total?.toFixed(2) })}</span>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Features List */}
        <div className="bg-gray-100 border border-gray-200 rounded-xl p-6">
          <h3 className="text-gray-900 font-semibold mb-4">{t('activation.whatsIncluded')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {features.map(({ icon: Icon, label }, i) => (
              <div key={i} className="flex items-center gap-3 text-gray-600 text-sm">
                <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-blue-400" />
                </div>
                {label}
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-gray-400 text-xs mt-6">
          {t('activation.secureFooter')}
        </p>
      </div>
    </div>
  );
}
